/**
 * O vocabulario de periodo do painel, e como cada atalho vira um intervalo.
 *
 * Quatro telas escolhem periodo - o painel, a ficha do paciente, a Adocao e Engajamento
 * e o Relatorio de Sono - e ate aqui cada uma tinha a sua aritmetica de "ultimos 30
 * dias". Duas copias da mesma conta e o jeito de duas telas discordarem sobre o que
 * e um mes sem ninguem decidir que deviam discordar.
 *
 * Quem MONTA o seletor declara QUAIS atalhos oferece; como cada um se calcula e
 * decidido aqui. E o que permite o Relatorio de Sono oferecer cinco atalhos curtos e
 * a Adocao e Engajamento oferecer os longos, sem nenhuma das duas reimplementar a conta.
 */
export const PERIOD_SHORTCUTS = [
    /**
     * SEM recorte de data - "desde sempre".
     *
     * Nao e um intervalo: `periodRange` devolve as duas pontas VAZIAS, e quem hospeda
     * entende isso como "nao mande periodo ao servidor". Existe porque os
     * Monitoramentos Compartilhados abrem sem filtro de data, e um seletor que sempre
     * mostra um intervalo nao teria como dizer "nenhum".
     *
     * So aparece onde quem monta o oferece.
     */
    'all',
    '1d',
    '7d',
    '14d',
    /**
     * Quinze dias - a janela do MEIO de "Ativo em", em Adocao e Engajamento.
     *
     * Vizinho de `14d` e nao substituto dele: quinze dias e meio mes, e quatorze sao
     * duas semanas. Os dois existem porque as duas contas existem em telas diferentes -
     * a ficha do paciente e o Relatorio de Sono raciocinam por semana, e os cartoes de
     * Adocao e Engajamento oferecem 7, 15 e 30.
     *
     * Nasceu para a lista de Colaboradores poder DIZER "Últimos 15 dias" quando o
     * cartao "Ativos (15 dias)" a abre. Sem ele no vocabulario, aquela janela chegava
     * como um par de datas que nenhum atalho reconhecia, e o seletor so sabia chama-la
     * de "Escolher datas".
     */
    '15d',
    '30d',
    '90d',
    'month',
    'last-month',
    '12m',
    'year',
    'custom'
];
/**
 * O nome de cada atalho.
 *
 * Por extenso, e nao abreviado: o seletor mostra o rotulo com a lista fechada, sem
 * um titulo por perto que complete o sentido - "30 dias" sozinho num combobox nao
 * diz se sao os proximos ou os passados.
 */
export const PERIOD_LABELS = {
    all: 'Todo o período',
    '1d': 'Hoje',
    '7d': 'Últimos 7 dias',
    '14d': 'Últimos 14 dias',
    '15d': 'Últimos 15 dias',
    '30d': 'Últimos 30 dias',
    '90d': 'Últimos 90 dias',
    month: 'Este mês',
    'last-month': 'Mês passado',
    '12m': 'Últimos 12 meses',
    year: 'Este ano',
    custom: 'Escolher datas'
};
/**
 * O nome CURTO, para quem desenha uma fileira em vez de uma lista.
 *
 * Numa fileira de pilulas o grupo ja se chama "Período", entao "Últimos" esta dito
 * no titulo e repeti-lo em quatro pilulas custa largura que faz a fileira quebrar em
 * duas linhas. Num combobox fechado nao ha titulo por perto, e o nome por extenso e
 * o que diz se os 30 dias sao os proximos ou os passados.
 *
 * Os dois existem porque as duas formas existem, e nao porque alguem se descuidou.
 */
export const PERIOD_SHORT_LABELS = {
    all: 'Tudo',
    '1d': 'Hoje',
    '7d': '7 dias',
    '14d': '14 dias',
    '15d': '15 dias',
    '30d': '30 dias',
    '90d': '90 dias',
    month: 'Este mês',
    'last-month': 'Mês passado',
    '12m': '12 meses',
    year: 'Este ano',
    custom: 'Escolher datas'
};
/** O dia de uma data, em `yyyy-mm-dd`. */
function day(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const dayOfMonth = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${dayOfMonth}`;
}
/**
 * O intervalo de um atalho, em `yyyy-mm-dd`.
 *
 * `today` entra por PARAMETRO para o teste nao depender do relogio - a mesma razao
 * pela qual o resto do painel passa a data de referencia em vez de chamar
 * `new Date()` no meio da regra.
 *
 * `custom` nao tem intervalo proprio: quem o escolhe ja tem duas datas. Devolve a
 * semana como ponto de partida, que e o menos surpreendente para quem chegou aqui
 * por um endereco truncado.
 */
export function periodRange(shortcut, today = new Date()) {
    // Meio-dia evita que o fuso empurre a data para o dia anterior.
    const base = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12);
    const lastDays = (count) => {
        const start = new Date(base);
        // `-1` porque o intervalo INCLUI hoje: "ultimos 7 dias" sao 7 linhas.
        start.setDate(start.getDate() - (count - 1));
        return { from: day(start), to: day(base) };
    };
    switch (shortcut) {
        // Sem recorte: as duas pontas vazias, e quem hospeda nao manda periodo.
        case 'all':
            return { from: '', to: '' };
        // Um dia so: as duas pontas no mesmo dia. E o que faz a serie sair minuto a
        // minuto, porque quem decide a granularidade e o TAMANHO do intervalo.
        case '1d':
            return lastDays(1);
        case '7d':
            return lastDays(7);
        case '14d':
            return lastDays(14);
        case '15d':
            return lastDays(15);
        case '30d':
            return lastDays(30);
        case '90d':
            return lastDays(90);
        case 'month':
            return { from: day(new Date(base.getFullYear(), base.getMonth(), 1, 12)), to: day(base) };
        case 'last-month': {
            const start = new Date(base.getFullYear(), base.getMonth() - 1, 1, 12);
            // Dia 0 do mes corrente e o ultimo dia do anterior.
            const end = new Date(base.getFullYear(), base.getMonth(), 0, 12);
            return { from: day(start), to: day(end) };
        }
        case '12m': {
            const start = new Date(base.getFullYear(), base.getMonth() - 11, 1, 12);
            return { from: day(start), to: day(base) };
        }
        case 'year':
            return { from: day(new Date(base.getFullYear(), 0, 1, 12)), to: day(base) };
        case 'custom':
            return lastDays(7);
    }
}
/**
 * O atalho que PRODUZ este intervalo, entre os que a tela oferece.
 *
 * O caminho de volta de `periodRange`, e existe porque nem todo intervalo chega de
 * um clique no seletor: os cartoes de Adocao e Engajamento abrem a lista de
 * Colaboradores com duas datas, e so com elas - o atalho nao viaja no endereco,
 * porque publicar atalho E datas abriria a porta para um endereco em que os dois
 * discordam.
 *
 * Sem esta funcao a lista so sabia chamar o que recebia de "Escolher datas", e quem
 * clicava em "Ativos (30 dias)" nao tinha como saber, olhando a tela, que estava
 * vendo justamente os ultimos 30 dias. Com ela o seletor abre escrito "Últimos 30
 * dias" - o mesmo nome do cartao de onde a pessoa veio.
 *
 * `custom` e a resposta honesta quando nada casa, e e o caso comum: a janela de um
 * cartao termina no fim do periodo APURADO, que so e hoje quando o relatorio esta
 * aberto no periodo corrente.
 *
 * `today` entra por parametro pela mesma razao de `periodRange`: teste que depende
 * do relogio passa hoje e falha amanha.
 */
export function shortcutOf(from, to, among, today = new Date()) {
    // Sem pontas nao ha intervalo: e "todo o periodo" para quem o oferece.
    if (!from && !to) {
        return among.includes('all') ? 'all' : 'custom';
    }
    for (const shortcut of among) {
        // Os dois que nao sao intervalo: `all` ja foi respondido acima, e `custom` e
        // o que sobra quando nenhum outro casa - compara-lo casaria a semana por
        // acidente, porque e o que `periodRange` devolve para ele.
        if (shortcut === 'all' || shortcut === 'custom') {
            continue;
        }
        const range = periodRange(shortcut, today);
        if (range.from === from && range.to === to) {
            return shortcut;
        }
    }
    return 'custom';
}
/**
 * O intervalo que de fato vale, depois do teto de quem hospeda.
 *
 * Encurta pelo COMECO e preserva o fim: quem pediu um ano querendo ver o mes que
 * passou prefere ver as ultimas cinco semanas a ver as cinco primeiras.
 *
 * `clamped` existe para a tela poder AVISAR. Encurtar calado faria o leitor achar
 * que faltam dados no periodo que ele pediu.
 */
export function clampRange(from, to, maxDays) {
    if (!maxDays) {
        return { from, to, clamped: false };
    }
    const start = new Date(`${from}T12:00:00`);
    const end = new Date(`${to}T12:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
        return { from, to, clamped: false };
    }
    const days = Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1;
    if (days <= maxDays) {
        return { from, to, clamped: false };
    }
    const newStart = new Date(end);
    newStart.setDate(newStart.getDate() - (maxDays - 1));
    return { from: day(newStart), to, clamped: true };
}
//# sourceMappingURL=period.types.js.map